


























import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { DirectiveOptions } from 'vue';

// this will set up the aria attributes for the dialog
const SetAriaLabelledByDirective: DirectiveOptions = {
  inserted(el, binding) {
    setTimeout(() => {
      const dialogElement = el.querySelector('.el-dialog');
      if (dialogElement) {
        dialogElement.setAttribute('aria-label', '');
        dialogElement.setAttribute('aria-labelledby', binding.value);
      }
    }, 100); // Ensure input is fully rendered before modifying it
  },
};

@Component({
  directives: {
    setAriaLabelledBy: SetAriaLabelledByDirective,
  },
})
export default class DialogCpt extends Vue {
  @Prop({ default: true }) showClose!: boolean;
  @Prop({ default: false }) showDialog!: boolean;
  @Prop({ required: true }) title!: string;
  @Prop({ default: '40%' }) width!: string;
  @Prop({ default: '15vh' }) top!: string;
  @Prop({ default: '' }) cssClass!: string;
  @Prop({ default: 'dialog-element' }) elementId!: string;

  isOpen = false;
  private focusTrapListener: ((event: KeyboardEvent) => void) | null = null;
  private previousFocusedElement: HTMLElement | null = null;

  // Watch for dialog visibility and open/close state
  @Watch('showDialog')
  onShowDialogChange(newVal: boolean) {
    this.isOpen = newVal;
  }

  mounted() {
    this.isOpen = this.showDialog;
  }

  // When the dialog opens, auto-focus and trap focus
  private onDialogOpened() {
    this.$nextTick(() => {
      const dialogElement = (this.$refs.dialogElement as any).$el as HTMLElement; // Access the DOM element

      // Store currently focused element before opening
      this.previousFocusedElement = document.activeElement as HTMLElement;

      this.setupFocusTrap(dialogElement);
      this.autoFocus(dialogElement);
    });
  }

  // Auto-focus the first focusable element or the dialog itself
  private autoFocus(dialogElement: HTMLElement) {
    const firstFocusableElement = this.getFocusableElements(dialogElement)[0];
    if (firstFocusableElement) {
      firstFocusableElement.focus();
    } else {
      dialogElement.focus();
    }
  }

  // Set up focus trap
  private setupFocusTrap(dialogElement: HTMLElement) {
    const focusableElements = this.getFocusableElements(dialogElement);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    this.focusTrapListener = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          event.preventDefault();
        }
      }
    };

    dialogElement.addEventListener('keydown', this.focusTrapListener);
  }

  // Get all focusable elements within the dialog
  private getFocusableElements(dialogElement: HTMLElement): HTMLElement[] {
    return Array.from(
      dialogElement.querySelectorAll<HTMLElement>(
        "button, a, input, select, textarea, [tabindex]:not([tabindex='-1'])",
      ),
    );
  }

  // Clean up focus trap when the dialog closes
  private removeFocusTrap() {
    const dialogElement = (this.$refs.dialogElement as any).$el as HTMLElement;
    if (this.focusTrapListener) {
      dialogElement.removeEventListener('keydown', this.focusTrapListener);
      this.focusTrapListener = null;
    }
  }

  // Handle dialog close event
  private onDialogClose() {
    this.isOpen = false;
    this.removeFocusTrap();

    if (this.previousFocusedElement) {
      this.previousFocusedElement.focus();
      this.previousFocusedElement = null; // Clear reference
    }

    this.$emit('close');
  }
}
